/**
 * API Configuration
 * Centralized configuration for all API endpoints and settings
 */

import {env} from './env';

// Environment-based API configuration
const getApiConfig = () => {
  const isDevelopment = env.MODE === 'development';
  const isProduction = env.MODE === 'production';

  // Base API URL - configured via environment variables
  // Priority: VITE_API_BASE_URL > Auto-detect based on mode > Fallback
  const baseUrl =
    env.VITE_API_BASE_URL ||
    (isDevelopment
      ? 'http://localhost:3001/api'
      : 'https://onedatauat01.azurewebsites.net/api');

  return {
    // Base API URL
    baseURL: baseUrl,

    // API endpoints
    endpoints: {
      // Authentication
      auth: {
        register: '/auth/register',
        login: '/auth/login',
        logout: '/auth/logout',
        me: '/auth/me',
        updatePassword: '/auth/update-password',
        sso: {
          callback: '/auth/sso/callback',
          centific: env.VITE_CENTIFIC_SSO_URL,
        },
        entra: {
          callback: '/auth/entra/callback',
        },
      },

      // User management
      users: {
        profile: '/users/profile',
        update: '/users/update',
        delete: '/users/delete',
        create: '/users/create',
        bulkCreate: '/users/bulk-create',
        getByEmail: (email) => `/users/email/${encodeURIComponent(email)}`,
        updateRole: (userId) => `/users/${userId}/role`,
      },

      // User roles
      userRoles: {
        get: (userId) => `/auth/user-roles/${userId}`,
        update: '/auth/user-roles',
      },

      // Moderators
      moderators: {
        list: '/moderators',
        get: (id) => `/moderators/${id}`,
        byEmail: (email) =>
          `/auth/moderators/email/${encodeURIComponent(email)}`,
        byEmpId: (empId) => `/auth/moderators/emp/${empId}`,
        byIds: '/moderators/by-ids',
        active: '/moderators/active',
        byProject: (projectId) => `/moderators/project/${projectId}`,
        mySessionChanges: (projectId) =>
          `/moderators/project/${projectId}/my-session-changes`,
        create: '/moderators',
        update: (id) => `/moderators/${id}`,
        delete: (id) => `/moderators/${id}`,
        register: '/auth/moderators/register',
      },

      // Participants
      participants: {
        list: '/participants',
        get: (id) => `/participants/${id}`,
        byEmail: (email) => `/participants/email/${encodeURIComponent(email)}`,
        create: '/participants',
        createBulk: '/participants/bulk',
        update: (id) => `/participants/${id}`,
        delete: (id) => `/participants/${id}`,
        // Send self-booking link email to participant
        sendBookingLink: (participantId) =>
          `/participants/${encodeURIComponent(participantId)}/send-booking-link`,
        // Send self-booking links to many participants for one session type at once
        bulkSendBookingLink: '/participants/bulk-send-booking-link',
        assignScheduler: '/participants/assign-scheduler',
        removeFromProject: (id, projectId) =>
          `/participants/${id}/project/${projectId}`,
        createRemoval: (id, projectId) =>
          `/participants/${id}/project/${projectId}/removals`,
        removalHistory: (projectId) =>
          `/participants/project/${projectId}/removals`,
        exportRemovals: (projectId) =>
          `/participants/project/${projectId}/removals/export`,
        stats: {
          statusCards: '/participants/stats/status-cards',
        },
        filterOptions: '/participants/filter-options',
        uploadSchema: '/participants/upload-schema',
      },

      // Projects
      projects: {
        list: '/projects',
        get: (id) => `/projects/${id}`,
        summary: (id: string) => `/projects/${id}/summary`,
        create: '/projects',
        update: (id) => `/projects/${id}`,
        delete: (id) => `/projects/${id}`,
        history: (id) => `/projects/${id}/history`,
        emailTemplateHistory: (id) => `/projects/${id}/email-template-history`,
        migrateDynamicsId: '/projects/migrate-dynamics-id',
      },

      // Project payment (bonus / partial payment) configuration — OD-1089
      paymentConfig: {
        get: (projectId) => `/projects/${projectId}/payment-config`,
        addSubcategory: (projectId) =>
          `/projects/${projectId}/payment-config/subcategories`,
        updateSubcategory: (projectId, id) =>
          `/projects/${projectId}/payment-config/subcategories/${id}`,
      },

      // Schedules
      scheduler: {
        /** Server-side Scheduler queue (filter/sort/paginate computed on the server). */
        queue: '/scheduler/queue',
        /** Server-side Booked Sessions list (Schedule page → Booked Sessions tab). */
        bookedQueue: '/scheduler/booked-queue',
        /** Server-side Booked Sessions KPI tiles (Schedule page). */
        bookedStats: '/scheduler/booked-stats',
        /** Bulk invite Step 1: active session types + count of participants that can be invited. */
        sessionTypesInvitable: '/scheduler/session-types/invitable',
        /** Bulk invite Step 2: invitable participants for one session type. */
        sessionTypeInvitableParticipants: (sessionTypeId: string) =>
          `/scheduler/session-types/${encodeURIComponent(sessionTypeId)}/invitable-participants`,
      },
      schedules: {
        list: '/schedules',
        statusCounts: '/schedules/status-counts',
        consolidatedExport: '/schedules/consolidated-export',
        contactLogExport: '/schedules/contact-log-export',
        get: (id) => `/schedules/${id}`,
        byParticipant: (participantId) =>
          `/schedules/by-participant/${participantId}`,
        byParticipants: '/schedules/by-participants',
        create: '/schedules',
        /** Atomic group book: multiple participants, one transaction */
        createGroup: '/schedules/group',
        /** Bulk cancel: many sessions, one reason + explanation, one transaction */
        bulkCancel: '/schedules/bulk-cancel',
        /** Assign one generated Group Booking ID to existing sessions. */
        assignGroupId: '/schedules/assign-group-id',
        /**
         * OD-1277 group booking cancellation — full cancel or partial
         * participant removal, one reason + explanation, one transaction.
         */
        groupCancel: (bookingGroupId: string) =>
          `/schedules/group/${encodeURIComponent(bookingGroupId)}/cancel`,
        createSimple: '/schedules/create',
        update: (id) => `/schedules/${id}`,
        actualTimes: (id: string) =>
          `/schedules/${encodeURIComponent(id)}/actual-times`,
        delete: (id) => `/schedules/${id}`,
        // New participant scheduling endpoints
        availableModerators: (projectId) =>
          `/schedules/available-moderators/${projectId}`,
        availableSlots: (moderatorId, projectId) =>
          `/schedules/available-slots/${moderatorId}/${projectId}`,
        slotCapacity: '/schedules/slot-capacity',
        participantBook: '/schedules/participant-book',
        participantCancel: (scheduleId) =>
          `/schedules/participant-cancel/${scheduleId}`,
        participantReschedule: (scheduleId) =>
          `/schedules/participant-reschedule/${scheduleId}`,
        participantActionValidate: '/schedules/participant-action/validate',
        participantActionCancel: '/schedules/participant-action/cancel',
        participantMeetings: (participantId) =>
          `/schedules/participant/${participantId}`,
        generateCheckInCode: (scheduleId) =>
          `/schedules/generate-check-in-code/${scheduleId}`,
        verifyCheckInCode: (scheduleId) =>
          `/schedules/verify-check-in-code/${scheduleId}`,
        checkInStatus: (scheduleId) =>
          `/schedules/check-in-status/${scheduleId}`,
        addNote: (scheduleId) => `/schedules/${scheduleId}/notes`,
        disqualify: (scheduleId) =>
          `/schedules/${encodeURIComponent(scheduleId)}/disqualify`,
        doNotHire: (scheduleId) =>
          `/schedules/${encodeURIComponent(scheduleId)}/do-not-hire`,
        overbook: (scheduleId) =>
          `/schedules/${encodeURIComponent(scheduleId)}/overbook`,
        correctStatus: (scheduleId: string) =>
          `/schedules/${encodeURIComponent(scheduleId)}/correct-status`,
        schedulerReminders: '/schedules/scheduler-reminders',
      },

      // Surveys
      surveys: {
        list: '/surveys',
        get: (id) => `/surveys/${id}`,
        create: '/surveys',
        update: (id) => `/surveys/${id}`,
        delete: (id) => `/surveys/${id}`,
        responses: {
          list: (surveyId) => `/surveys/${surveyId}/responses`,
          create: (surveyId) => `/surveys/${surveyId}/responses`,
        },
      },

      // Tasks
      tasks: {
        list: '/tasks',
        get: (id) => `/tasks/${id}`,
        byProject: (projectId) => `/tasks/project/${projectId}`,
        create: '/tasks',
        update: (id) => `/tasks/${id}`,
        delete: (id) => `/tasks/${id}`,
        toggleActive: (id) => `/tasks/${id}/toggle-active`,
      },

      // Task View Templates (@onedata/task-be)
      taskModule: {
        projects: '/task-module/templates/projects',
        get: (projectId: string) => `/task-module/templates/${projectId}`,
        preview: (projectId: string) => `/task-module/templates/${projectId}/preview`,
        publish: (projectId: string) => `/task-module/templates/${projectId}/publish`,
        dataFields: {
          get: (projectId: string) => `/task-module/data-fields/${projectId}`,
          preview: (projectId: string) => `/task-module/data-fields/${projectId}/preview`,
          publish: (projectId: string) => `/task-module/data-fields/${projectId}/publish`,
        },
        tasks: {
          list: (projectId: string) => `/task-module/projects/${projectId}/tasks`,
          create: (projectId: string) => `/task-module/projects/${projectId}/tasks`,
          get: (taskId: string) => `/task-module/tasks/${taskId}`,
          update: (taskId: string) => `/task-module/tasks/${taskId}`,
          updateDefinition: (taskId: string) =>
            `/task-module/tasks/${taskId}/definition`,
          delete: (taskId: string) => `/task-module/tasks/${taskId}`,
        },
      },

      // Journal Activity
      journalActivity: {
        list: '/journal-activity',
        get: (id) => `/journal-activity/${id}`,
        getByHomeAndDate: (homeId, date) =>
          `/journal-activity/home/${homeId}/date/${date}`,
        create: '/journal-activity',
        createBulk: '/journal-activity/bulk',
        update: (id) => `/journal-activity/${id}`,
        delete: (id) => `/journal-activity/${id}`,
        deleteByHomeAndDate: (homeId, date) =>
          `/journal-activity/home/${homeId}/date/${date}`,
      },

      // Journal Media
      journalMedia: {
        create: '/journal-media',
        getByActivity: (activityId) => `/journal-media/activity/${activityId}`,
        countsByActivity: (activityId) =>
          `/journal-media/activity/${activityId}/counts`,
      },

      // Project Moderators
      projectModerators: {
        list: '/project-moderators',
        get: (id) => `/project-moderators/${id}`,
        create: '/project-moderators',
        update: (id) => `/project-moderators/${id}`,
        delete: (id) => `/project-moderators/${id}`,
      },

      // Booking decisions (OD-1102 follow-up): PM allow/block decision on a
      // participant continuing to be booked/invited for a specific session type.
      bookingDecisions: {
        list: '/booking-decisions',
        upsert: '/booking-decisions',
      },

      // Schedule Locations
      scheduleLocations: {
        list: '/schedule-locations',
        get: (id) => `/schedule-locations/${id}`,
        byProject: (projectId) => `/schedule-locations/project/${projectId}`,
        create: '/schedule-locations',
        update: (id) => `/schedule-locations/${id}`,
        delete: (id) => `/schedule-locations/${id}`,
        toggle: (id) => `/schedule-locations/${id}/toggle`,
      },

      // Session Types
      sessionTypes: {
        list: '/session-types',
        get: (id) => `/session-types/${id}`,
        detailed: '/session-types/detailed/all',
        jobsByProject: (projectId) => {
          const id = Array.isArray(projectId) ? projectId[0] : projectId;
          return `/session-types/project/${id}/jobs`;
        },
        refreshJobRate: (projectId, jobId) => {
          const normalizedProjectId = Array.isArray(projectId)
            ? projectId[0]
            : projectId;
          return `/session-types/project/${normalizedProjectId}/jobs/${jobId}/refresh-rate`;
        },
        participantJobAssignmentsByProject: (projectId) => {
          const id = Array.isArray(projectId) ? projectId[0] : projectId;
          return `/session-types/project/${id}/participant-job-assignments`;
        },
        byProject: (projectId) => {
          // Ensure projectId is a single UUID string, not an array
          const id = Array.isArray(projectId) ? projectId[0] : projectId;
          return `/session-types/project/${id}`;
        },
        byProjectDetailed: (projectId) => {
          // Ensure projectId is a single UUID string, not an array
          const id = Array.isArray(projectId) ? projectId[0] : projectId;
          return `/session-types/project/${id}/detailed`;
        },
        create: '/session-types',
        update: (id) => `/session-types/${id}`,
        delete: (id) => `/session-types/${id}`,
        toggle: (id) => `/session-types/${id}/toggle`,
        history: (id) => `/session-types/${id}/history`,
      },

      // OD-1332: upload batch outcomes and their persistent notifications.
      uploadNotifications: {
        list: '/upload-notifications',
        report: '/upload-notifications/batches',
        markRead: (id: string) => `/upload-notifications/${id}/read`,
        markAllRead: '/upload-notifications/read-all',
        actionItems: '/upload-notifications/action-items',
        getBatch: (ref: string) => `/upload-notifications/batches/${ref}`,
        dismissBatch: (id: string) => `/upload-notifications/batches/${id}/dismiss`,
      },

      // Team
      submissionMetadata: {
        create: '/submission-metadata',
        update: (id) => `/submission-metadata/${id}`,
        delete: (id) => `/submission-metadata/${id}`,
        restore: (id) => `/submission-metadata/${id}/restore`,
        deleteBackup: (id) => `/submission-metadata/${id}/backup`,
        getTasks: '/submission-metadata/tasks',
        getSessions: '/submission-metadata/sessions',
        getById: (id) => `/submission-metadata/by-id/${id}`,
        getDataUploads: '/submission-metadata/data-uploads',
      },

      // Public Booking Links (self-booking)
      bookingLinks: {
        validate: (token: string) =>
          `/booking-links/${encodeURIComponent(token)}/validate`,
        confirmEmail: (token: string) =>
          `/booking-links/${encodeURIComponent(token)}/confirm-email`,
        sendOtp: (token: string) =>
          `/booking-links/${encodeURIComponent(token)}/send-otp`,
        verifyOtp: (token: string) =>
          `/booking-links/${encodeURIComponent(token)}/verify-otp`,
        getTimeSlots: (token: string) =>
          `/booking-links/${encodeURIComponent(token)}/time-slots`,
        book: (token: string) =>
          `/booking-links/${encodeURIComponent(token)}/book`,
      },

      // Session Prep Drafts
      sessionPrepDrafts: {
        create: '/session-prep-drafts',
        get: '/session-prep-drafts',
        getBySchedule: (scheduleId) =>
          `/session-prep-drafts/schedule/${scheduleId}`,
        delete: '/session-prep-drafts',
      },

      team: {
        byProject: (projectId) => `/team/project/${projectId}`,
        stats: (projectId) => `/team/project/${projectId}/stats`,
        addMember: (projectId) => `/team/project/${projectId}/members`,
        history: (projectId) => `/team/project/${projectId}/history`,
        updateMember: (memberId) => `/team/members/${memberId}`,
        removeMember: (memberId) => `/team/members/${memberId}`,
        getSchedulers: (projectId?: string) =>
          projectId
            ? `/team/schedulers?project_id=${projectId}&status=active`
            : `/team/schedulers?status=active`,
        availableUsers: '/team/available-users',
        acceptInvitation: (token: string) =>
          `/team/accept-invitation?token=${encodeURIComponent(token)}`,
        // OD-1114: apply one set of configurable-column values to many members.
        bulkFields: (projectId: string) =>
          `/team/project/${projectId}/members/bulk-fields`,
      },

      // OD-1114: configurable module views (column set per project + module).
      viewColumns: {
        get: (projectId: string, module: string) =>
          `/view-columns/${projectId}/${module}`,
        save: (projectId: string, module: string) =>
          `/view-columns/${projectId}/${module}`,
        template: (projectId: string, module: string) =>
          `/view-columns/${projectId}/${module}/template`,
        previewTemplate: (projectId: string, module: string) =>
          `/view-columns/${projectId}/${module}/template/preview`,
      },

      // Feedback
      feedback: {
        list: '/feedback',
        get: (id) => `/feedback/${id}`,
        create: '/feedback',
        update: (id) => `/feedback/${id}`,
        delete: (id) => `/feedback/${id}`,
        responses: {
          list: (feedbackId) => `/feedback/${feedbackId}/responses`,
          create: (feedbackId) => `/feedback/${feedbackId}/responses`,
        },
      },

      // Health check
      health: '/health',

      // Activity logs
      activityLogs: {
        user: '/auth/activity-logs',
        all: '/auth/all-activity-logs',
      },
    },

    // Request configuration
    request: {
      timeout: 30000, // 30 seconds
      /**
       * Timeout for the LLM-backed AI Setup calls (guideline parse, chat).
       *
       * The 30s default aborts these client-side while the server is still
       * working: parsing a full guideline runs ~40s on Opus, and a chat turn
       * that uses tools takes several round-trips. Nothing retries the call, so
       * a client-side abort throws away a completed server-side parse.
       */
      llmTimeout: 180000, // 3 minutes
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
    },

    // Response configuration
    response: {
      timeout: 30000,
      retries: 3,
      retryDelay: 1000,
    },

    // Environment info
    environment: {
      isDevelopment,
      isProduction,
      mode: env.MODE,
    },
  };
};

// Export the configuration
export const apiConfig = getApiConfig();

// Helper function to build full API URLs
export const buildApiUrl = (endpoint) => {
  return `${apiConfig.baseURL}${endpoint}`;
};

// Helper function to get endpoint with parameters
export const getEndpoint = (path, params = {}) => {
  let endpoint = path;

  // Replace path parameters
  Object.keys(params).forEach((key) => {
    endpoint = endpoint.replace(`:${key}`, params[key]);
  });

  return buildApiUrl(endpoint);
};

// Export commonly used endpoints
export const {endpoints, baseURL, environment} = apiConfig;

// Default export
export default apiConfig;
